/*
 * The name map: every icon the product may draw, and the lucide drawing behind it.
 *
 * This file is the closed set. A name that is not here is a compile error at the call site, and a
 * lucide name that is not a file in the icon set is a build error in `make gen-icons` — so the only
 * way an icon enters the interface is a line added here and a regenerated `icons.gen.ts`. The
 * drawings themselves never live in this tree: the generator reads them out of the `lucide-static`
 * development dependency and writes one `d` string per lucide name into `icons.gen.ts`.
 *
 * Why the two names differ: the left column is what a surface asks for ("the composer's bullet
 * list"), the right one is what lucide calls the picture ("list"). Renaming the picture is then a
 * one-line change here rather than a sweep over every caller, and two teamBase names may share one
 * drawing — `star` and `starFilled` are the same closed path, painted differently (`icons.tsx`).
 *
 * The groups are the order the set is used in: the rail's places, the chrome every surface reaches
 * for, the composer's formatting row, the actions a row or a menu offers, and the names the shell's
 * menus, alerts, toasts and empty states need.
 */

export const ICONS = {
	// The rail's places.
	home: "house",
	activity: "activity",
	search: "search",
	people: "users",
	admin: "shield-check",
	/* AI Chats: the sparkles most products give an assistant. `code` would have said "developers
	   only", and most AI chats are questions about the code rather than changes to it. */
	aichat: "sparkles",

	// The chrome: what a header, a row, a dialog or a menu offers.
	close: "x",
	/* The generic "onward" chevron; `chevronRight` is the same picture under the name a pair uses. */
	chevron: "chevron-right",
	chevronDown: "chevron-down",
	chevronLeft: "chevron-left",
	chevronRight: "chevron-right",
	more: "ellipsis",
	plus: "plus",
	lock: "lock",
	reply: "reply",
	info: "info",

	// The composer's formatting row.
	textStyle: "case-sensitive",
	bold: "bold",
	italic: "italic",
	strike: "strikethrough",
	bulletList: "list",
	orderedList: "list-ordered",
	code: "code",
	codeBlock: "square-code",
	quote: "text-quote",
	link: "link",
	/* Two blocks beyond the formatting row, which an AI chat's plan, its to-do list and its
	   workflow draw: the checklist and the divider. */
	checklist: "list-checks",
	divider: "minus",
	emoji: "smile",
	paperclip: "paperclip",
	send: "send-horizontal",
	sourceCode: "file-code",
	/* What a document's toolbar has beyond the composer's row (DV S-01 §3.5): undo and redo, the
	   two marks a message lacks, the table, a new comment and *Clear formatting*. */
	undo: "undo-2",
	redo: "redo-2",
	underline: "underline",
	highlight: "highlighter",
	table: "table",
	commentAdd: "message-square-plus",
	clearFormat: "remove-formatting",

	// The actions a row, a message or a conversation offers.
	edit: "pencil",
	check: "check",
	download: "download",
	eye: "eye",
	eyeOff: "eye-off",
	star: "star",
	starFilled: "star",
	bell: "bell",
	bellOff: "bell-off",
	pin: "pin",
	file: "file",
	/* A written page. `file` is the generic sheet a row or a menu uses; this one carries the lines
	   of written text, which is what tells a page from a file attached to a message. */
	document: "file-text",
	forward: "forward",
	/*
	 * One thread and the list of them are two pictures on purpose. `thread` is a single bubble with
	 * lines in it — the conversation held under one message — and every surface that offers or names
	 * *that* thread wears it; `threads` is the stack of bubbles the *Threads* place carries, where a
	 * reader goes to see all of them. A left-turning arrow (`reply`) said "answer this line here",
	 * which is the one thing the action does not do.
	 */
	thread: "message-square-text",
	threads: "messages-square",

	// The account menu, the details footer, alerts and toasts, the theme cards, the filters and the
	// states a list can be in.
	hash: "hash",
	at: "at-sign",
	trash: "trash-2",
	copy: "copy",
	settings: "settings",
	logOut: "log-out",
	userPlus: "user-plus",
	userMinus: "user-minus",
	circleCheck: "circle-check",
	triangleAlert: "triangle-alert",
	circleAlert: "circle-alert",
	sun: "sun",
	moon: "moon",
	monitor: "monitor",
	arrowUp: "arrow-up",
	arrowDown: "arrow-down",
	arrowLeft: "arrow-left",
	filter: "filter",
	image: "image",
	archive: "archive",
	messageSquare: "message-square",
	loader: "loader-circle",
	externalLink: "external-link",
	clock: "clock",
	keyboard: "keyboard",

	// What a page's band and its lists say about the state of the work: something newer to take is
	// the two turning arrows, a pause is the pause bars, a project's directory is a folder, and the
	// band's first control folds the list beside it.
	refresh: "refresh-cw",
	pause: "pause",
	folder: "folder",
	panelLeft: "panel-left",
	/* One tab of a document (DV W-2 §6): the band's tab menu, the tab rows and a tab's chip. A window
	   with its title bar, which reads as a page among pages rather than as a file or a folder. */
	tab: "app-window",
	// How a list of items is drawn: rows, or a grid of tiles (the drive's folder view).
	viewList: "list",
	viewGrid: "layout-grid",

	// An AI chat's transcript, status bar and settings. `claude` marks what Claude wrote and
	// `fromTeamBase` what teamBase itself said to Claude, so the two voices never share a glyph;
	// the two modes are what the chat may do — look at the code, or change it.
	claude: "sparkles",
	fromTeamBase: "shield-check",
	stop: "circle-stop",
	preview: "monitor",
	terminal: "terminal",
	workflow: "workflow",
	helper: "bot",
	thinking: "brain",
	note: "sticky-note",
	restore: "history",
	compact: "fold-vertical",
	public: "globe",
	modeRead: "eye",
	modeModify: "pencil",
	personal: "user-round",
} as const

/** Every name a caller may ask `Icon` for. */
export type IconName = keyof typeof ICONS
